/**
 * Data hook for activating a requested chat (issue #123).
 *
 * Activation is an explicit status transition — `chatUpdate({ status: 'active' })`
 * — and is intentionally separate from the `enabled` toggle (see
 * useUpdateChatEnabled). The mutation payload carries ONLY `status`, so it can
 * never be confused with, or accidentally flip, `enabled`.
 *
 * After a successful activation the chat is refetched (GetChatData) so the UI
 * reconciles to the server-true status/entities.
 */
import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { useChatUpdateDataMutationMutation } from '../../../generated';

export interface UseActivateChatConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface UseActivateChatResponse {
  activateChat: (variables: { chatId: string }) => Promise<void>;
  isActivating: boolean;
  error: Error | null;
}

export const useActivateChat = ({
  onSuccess,
  onError,
}: UseActivateChatConfig = {}): UseActivateChatResponse => {
  const { addNotification } = useNotification();

  const [activateChatMutation, { loading: isActivating, error }] =
    useChatUpdateDataMutationMutation({
      errorPolicy: 'all',
      // Reconcile the whole chat (status + entities) from the server after
      // activation rather than trusting a partial local write.
      refetchQueries: ['GetChatData'],
      awaitRefetchQueries: true,
    });

  const activateChat = useCallback(
    async ({ chatId }: { chatId: string }) => {
      try {
        const { data } = await activateChatMutation({
          variables: { id: chatId, chat: { status: 'active' } },
        });

        if (!data?.chatUpdate) {
          throw new Error('Activation returned no chat data');
        }

        addNotification({
          title: 'Chat Activated',
          message: 'The requested chat is now active.',
          type: 'success',
        });

        onSuccess?.();
      } catch (err) {
        const activationError =
          err instanceof Error ? err : new Error('Failed to activate chat');

        addNotification({
          title: 'Activation Failed',
          message: activationError.message,
          type: 'error',
        });

        onError?.(activationError);
        throw activationError;
      }
    },
    [activateChatMutation, addNotification, onSuccess, onError]
  );

  return {
    activateChat,
    isActivating,
    error: error ? new Error(error.message) : null,
  };
};
